import calculosBoletaService from '../services/calculosBoletaService';
import plameConfiguracionService from '../services/plameConfiguracionService';
import formulaService from '../services/formulaService';
import afpService from '../services/afpService';
import informacionGeneralService from '../services/informacionGeneralService';
import tramoRentaService from '../services/tramoRentaService';
import configuracionRentaQuintaService from '../services/configuracionRentaQuintaService';
import regimenLaboralService from '../services/regimenLaboralService';
import { esRegimenConstruccionCivil } from '../services/calculoRemuneracionConstruccionCivil';

export const CODIGO_SUELDO_BASE = '0121';
export const CODIGO_BONO_PRODUCTIVIDAD = '0902';

export function normalizarMonto(valor) {
  const n = parseFloat(valor);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

export function conceptosPlamePorTipo(empleado, tipoConcepto) {
  return (empleado?.conceptos || []).filter(
    (c) =>
      c.tipo_concepto === tipoConcepto &&
      String(c.tipo_boleta || 'plame').toLowerCase() === 'plame'
  );
}

export function ingresosPlameDeEmpleado(empleado) {
  return conceptosPlamePorTipo(empleado, 'ingreso');
}

function mapearConceptoEmpleadoAVista(c, celdas) {
  const codigo = c.plame_configuracion?.codigo_plame || '';
  return {
    id: c.id,
    plame_configuracion_id: c.plame_configuracion_id,
    codigoPlame: codigo,
    codigo: codigo,
    concepto: c.plame_configuracion?.descripcion || '',
    descripcion: c.plame_configuracion?.descripcion || '',
    monto: celdas[c.id]?.monto ?? normalizarMonto(c.monto),
  };
}

function resolverNombreAfp(emp) {
  const nombre = emp.afp?.nombre || '';
  if (!nombre) return '';
  return String(nombre).replace(/^AFP\s+/i, '').trim();
}

export async function cargarDependenciasCalculoMasivo(empresaId) {
  const [
    plameRes,
    formulasRes,
    afpsRes,
    infoRes,
    tramosRes,
    rentaRes,
    regimenesRes,
  ] = await Promise.all([
    plameConfiguracionService.obtenerConfiguraciones({ per_page: 1000 }),
    formulaService.obtenerFormulas(),
    afpService.obtenerAfps(),
    informacionGeneralService.obtenerInformacionActual(),
    tramoRentaService.obtenerTramosRenta(),
    configuracionRentaQuintaService.obtenerConfiguracion(empresaId),
    regimenLaboralService.getAll(),
  ]);

  const conceptosPlame = (plameRes?.data?.data || []).map((config) => ({
    id: config.id,
    codigo: config.codigo_plame,
    codigo_plame: config.codigo_plame,
    descripcion: config.descripcion,
    clasificacion: config.clasificacion,
    id_tipo: config.clasificacion?.id_tipo || null,
    afectaciones: {
      essalud_regular: config.essalud_regular,
      essalud_cbssp_seg: config.essalud_cbssp_seg,
      essalud_agrario: config.essalud_agrario,
      essalud_sctr: config.essalud_sctr,
      impuesto_extraord_solid: config.impuesto_extraord_solid,
      fondo_derechos_artista: config.fondo_derechos_artista,
      senati: config.senati,
      sistema_nacional_19990: config.sistema_nacional_19990,
      sistema_privado_pension: config.sistema_privado_pension,
      renta_5ta_categoria: config.renta_5ta_categoria,
    },
    essalud_regular: config.essalud_regular,
    sistema_nacional_19990: config.sistema_nacional_19990,
    sistema_privado_pension: config.sistema_privado_pension,
    renta_5ta_categoria: config.renta_5ta_categoria,
  }));

  const formulas = Array.isArray(formulasRes?.data?.data)
    ? formulasRes.data.data
    : Array.isArray(formulasRes?.data)
      ? formulasRes.data
      : [];
  const afpsRaw = afpsRes?.success !== false ? afpsRes : { data: afpsRes?.data };
  const afps = Array.isArray(afpsRaw?.data)
    ? afpsRaw.data
    : Array.isArray(afpsRaw?.data?.data)
      ? afpsRaw.data.data
      : [];
  const informacionGeneral = infoRes?.success ? infoRes.data : {};
  const tramosRenta = tramosRes?.data?.data || tramosRes?.data || [];
  const configuracionRentaQuinta = rentaRes?.success
    ? rentaRes.data
    : { activo: false };
  const regimenesLaborales = regimenesRes?.success
    ? regimenesRes.data
    : regimenesRes?.data || [];

  return {
    conceptosPlame,
    formulas,
    afps,
    informacionGeneral,
    tramosRenta,
    configuracionRentaQuinta,
    regimenesLaborales,
  };
}

function construirDatosCompletos(emp, celdas, deps, datosEmpresa) {
  const regimenNombre = emp.regimen_laboral?.nombre || '';
  const ingresos = conceptosPlamePorTipo(emp, 'ingreso').map((c) =>
    mapearConceptoEmpleadoAVista(c, celdas)
  );
  const descuentos = conceptosPlamePorTipo(emp, 'descuento').map((c) =>
    mapearConceptoEmpleadoAVista(c, celdas)
  );
  const aportes = conceptosPlamePorTipo(emp, 'aporte').map((c) =>
    mapearConceptoEmpleadoAVista(c, celdas)
  );

  const sueldo0121 =
    ingresos.find((i) => String(i.codigoPlame) === CODIGO_SUELDO_BASE)?.monto ??
    normalizarMonto(emp.sueldo_basico);

  const monto0902EnIngresos = ingresos.find(
    (i) => String(i.codigoPlame) === CODIGO_BONO_PRODUCTIVIDAD
  )?.monto;
  const tieneBonoCalculo =
    monto0902EnIngresos !== undefined && monto0902EnIngresos !== null
      ? monto0902EnIngresos > 0
      : Boolean(emp.tiene_bono);
  const montoBonoCalculo =
    monto0902EnIngresos !== undefined && monto0902EnIngresos !== null
      ? normalizarMonto(monto0902EnIngresos)
      : normalizarMonto(emp.monto_bono);

  const afpNombre = resolverNombreAfp(emp);

  return {
    datosEmpresa,
    datosAPIs: {
      regimenesLaborales: deps.regimenesLaborales,
      regimenSeleccionado: deps.regimenesLaborales.find((r) => r.nombre === regimenNombre),
      conceptosPlame: deps.conceptosPlame,
      conceptosIngresos: deps.conceptosPlame.filter((c) => c.id_tipo === 1),
      conceptosDescuentos: deps.conceptosPlame.filter((c) => c.id_tipo === 2),
      conceptosAportes: deps.conceptosPlame.filter((c) => c.id_tipo === 3),
      formulas: deps.formulas,
      formulasIngresos: deps.formulas.filter((f) => f.id_tipo === 1),
      formulasDescuentos: deps.formulas.filter((f) => f.id_tipo === 2),
      formulasAportes: deps.formulas.filter((f) => f.id_tipo === 3),
      afps: deps.afps,
      uit: deps.informacionGeneral?.uit || null,
      sueldoMinimo: deps.informacionGeneral?.sueldo_minimo || null,
      informacionGeneral: deps.informacionGeneral,
      tramosRenta: deps.tramosRenta,
      configuracionRentaQuinta: deps.configuracionRentaQuinta,
      rentaQuintaActivada: deps.configuracionRentaQuinta?.activo || false,
    },
    datosSalariales: {
      sueldoBasico: sueldo0121,
      sueldoInterno: normalizarMonto(emp.sueldo_interno),
      tipoPlanilla: emp.tipo_planilla || null,
      sistemaPensionario: emp.sistema_pensionario || 'ONP',
      afp: afpNombre,
      afpId: emp.afp_id || null,
      tipoAfp: emp.tipo_afp || 'Flujo',
      cuspp: emp.cuspp || '',
      regimenLaboral: regimenNombre,
      asignacionFamiliar: Boolean(emp.asignacion_familiar),
      tieneBono: tieneBonoCalculo,
      montoBono: montoBonoCalculo,
      esBoletaLiquidada: false,
      tipoContratacion: emp.tipo_contratacion || 'Planilla',
    },
    conceptosUsuario: { ingresos, descuentos, aportes },
    datosEmpleado: {
      fechaNacimiento: emp.fecha_nacimiento,
      afp_id: emp.afp_id,
      afp: emp.afp,
      estado: emp.estado,
    },
  };
}

function lineasBoletaAActualizaciones(emp, lineas) {
  const arr = Array.isArray(lineas) ? lineas : lineas ? [lineas] : [];
  const porPlameId = new Map();
  (emp.conceptos || []).forEach((c) => {
    if (c.plame_configuracion_id) {
      porPlameId.set(Number(c.plame_configuracion_id), c.id);
    }
  });
  const porCodigo = new Map();
  (emp.conceptos || []).forEach((c) => {
    const cod = c.plame_configuracion?.codigo_plame;
    if (cod) porCodigo.set(String(cod), c.id);
  });

  const updates = [];
  arr.forEach((linea) => {
    const plameId = linea.id_concepto?.id;
    const codigo = String(
      linea.id_concepto?.codigo_plame || linea.id_concepto?.codigo || ''
    );
    const conceptoId =
      (plameId != null && porPlameId.get(Number(plameId))) ||
      (codigo && porCodigo.get(codigo));
    if (!conceptoId) return;
    updates.push({
      empleado_id: emp.id,
      concepto_id: conceptoId,
      monto: normalizarMonto(linea.valor),
    });
  });
  return updates;
}

function deduplicarCambios(cambios) {
  const map = new Map();
  cambios.forEach((c) => {
    map.set(`${c.empleado_id}:${c.concepto_id}`, c);
  });
  return Array.from(map.values());
}

/**
 * Recalcula descuentos/aportes (y ingresos derivados) con el mismo motor que la ficha del empleado.
 */
export async function recalcularActualizacionesEmpleado(emp, celdas, deps, datosEmpresa) {
  const regimenNombre = emp.regimen_laboral?.nombre || '';
  if (
    esRegimenConstruccionCivil(
      { regimenLaboral: regimenNombre, regimen_laboral: emp.regimen_laboral },
      deps.regimenesLaborales
    )
  ) {
    return {
      ok: false,
      soloIngresos: true,
      mensaje: 'CONSU: solo se actualizan ingresos (recalcule planilla o ficha individual)',
      cambios: cambiosSoloIngresosEditados(emp, celdas),
    };
  }

  const datosCompletos = construirDatosCompletos(emp, celdas, deps, datosEmpresa);
  const resultado = await calculosBoletaService.generarCalculos(
    datosCompletos,
    'agregar_concepto_automatico'
  );

  if (!resultado?.success || !resultado.data) {
    return {
      ok: false,
      soloIngresos: true,
      mensaje: resultado?.error || 'No se pudo recalcular la boleta',
      cambios: cambiosSoloIngresosEditados(emp, celdas),
    };
  }

  const data = resultado.data;
  let cambios = [
    ...lineasBoletaAActualizaciones(emp, data.dataIngresos),
    ...lineasBoletaAActualizaciones(emp, data.dataDescuentos),
    ...lineasBoletaAActualizaciones(emp, data.dataAportes),
  ];
  cambios = deduplicarCambios(cambios);

  return { ok: true, cambios, mensaje: null };
}

export function cambiosSoloIngresosEditados(emp, celdas) {
  return ingresosPlameDeEmpleado(emp)
    .filter((c) => {
      const cell = celdas[c.id];
      return cell && cell.monto !== cell.original;
    })
    .map((c) => ({
      empleado_id: emp.id,
      concepto_id: c.id,
      monto: celdas[c.id].monto,
    }));
}

export async function armarCambiosConRecalculo(empleados, celdas, deps, datosEmpresa) {
  const idsAfectados = new Set(
    Object.values(celdas)
      .filter((cell) => cell.monto !== cell.original)
      .map((cell) => cell.empleadoId)
  );

  const advertencias = [];
  let todosLosCambios = [];

  for (const empleadoId of idsAfectados) {
    const emp = empleados.find((e) => e.id === empleadoId);
    if (!emp) continue;
    const res = await recalcularActualizacionesEmpleado(emp, celdas, deps, datosEmpresa);
    if (res.mensaje && !res.ok) {
      advertencias.push(`${emp.nombre_completo}: ${res.mensaje}`);
    }
    todosLosCambios = todosLosCambios.concat(res.cambios);
  }

  return {
    cambios: deduplicarCambios(todosLosCambios),
    advertencias,
  };
}
